var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as signalR from "@microsoft/signalr";
import { BackgroundTasksState } from "@/app/store/modules/background-tasks.state";
import { ProgressMessagesClient, ProgressMessageModel } from "@/app/api/battery-portal-api";
import { BackgroundTaskFinishedEvent, BackgroundTaskStatus, isFinishedStatus } from "@/app/models/background-task";
import { Notifier } from "@/common/utils/notifier";
const progressMessagesClient = new ProgressMessagesClient(window.apiUrl);
/** Remembered for the browser session, so a closed panel stays closed when the user moves between pages or reloads. */
const PanelOpenKey = "assure.backgroundTasks.panelOpen";
const AcknowledgedKey = "assure.backgroundTasks.acknowledged";
let connection = null;
function readSession(key, fallback) {
    try {
        const value = sessionStorage.getItem(key);
        return value === null ? fallback : JSON.parse(value);
    }
    catch (_a) {
        return fallback;
    }
}
function writeSession(key, value) {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    }
    catch (_a) {
        // Private mode or full storage: the panel just forgets between pages.
    }
}
export const state = new BackgroundTasksState();
export const mutations = {
    setMessages(state, messages) {
        state.messages = messages !== null && messages !== void 0 ? messages : [];
        // Forget acknowledgements of tasks that are gone.
        state.acknowledgedIds = state.acknowledgedIds.filter(id => state.messages.some(m => m.id === id));
        writeSession(AcknowledgedKey, state.acknowledgedIds);
    },
    /** Signed out: forget the list (what was acknowledged stays in the session). */
    clear(state) {
        state.messages = [];
        state.cancellingIds = [];
    },
    upsertMessage(state, message) {
        const index = state.messages.findIndex(m => m.id === message.id);
        if (index >= 0)
            state.messages.splice(index, 1, message);
        else
            state.messages.unshift(message);
        if (isFinishedStatus(message.status))
            state.cancellingIds = state.cancellingIds.filter(id => id !== message.id);
    },
    removeMessages(state, ids) {
        state.messages = state.messages.filter(m => !ids.includes(m.id));
        state.acknowledgedIds = state.acknowledgedIds.filter(id => !ids.includes(id));
        writeSession(AcknowledgedKey, state.acknowledgedIds);
    },
    setPanelOpen(state, open) {
        state.panelOpen = open;
        writeSession(PanelOpenKey, open);
    },
    addCancelling(state, id) {
        if (!state.cancellingIds.includes(id))
            state.cancellingIds.push(id);
    },
    removeCancelling(state, id) {
        state.cancellingIds = state.cancellingIds.filter(c => c !== id);
    },
    acknowledge(state, id) {
        if (!state.acknowledgedIds.includes(id)) {
            state.acknowledgedIds.push(id);
            writeSession(AcknowledgedKey, state.acknowledgedIds);
        }
    },
    restoreSession(state) {
        state.panelOpen = readSession(PanelOpenKey, true);
        state.acknowledgedIds = readSession(AcknowledgedKey, []);
    }
};
export const actions = {
    /** Loads the user's tasks and follows them live. Called once the user is signed in; safe to call again. */
    start(_a) {
        return __awaiter(this, arguments, void 0, function* ({ commit, dispatch }) {
            if (connection)
                return;
            commit("restoreSession");
            connection = new signalR.HubConnectionBuilder()
                .withUrl("/hubs/progress")
                .withAutomaticReconnect()
                .build();
            connection.on("ReceiveProgressMessage", (data) => dispatch("receive", ProgressMessageModel.fromJS(data)));
            // Anything that changed while disconnected (for example a task that finished) is read again.
            connection.onreconnected(() => dispatch("load"));
            yield dispatch("load");
            try {
                yield connection.start();
            }
            catch (_b) {
                // Not signed in any more, or the server is unreachable; the next page load tries again.
            }
        });
    },
    stop(_a) {
        return __awaiter(this, arguments, void 0, function* ({ commit }) {
            const current = connection;
            connection = null;
            commit("clear");
            try {
                yield (current === null || current === void 0 ? void 0 : current.stop());
            }
            catch (_b) {
                // Already closed.
            }
        });
    },
    load(_a) {
        return __awaiter(this, arguments, void 0, function* ({ commit }) {
            var _b;
            try {
                commit("setMessages", (_b = yield progressMessagesClient.getForUser()) !== null && _b !== void 0 ? _b : []);
            }
            catch (_c) {
                // The panel is secondary; a failed load must not disturb the page.
            }
        });
    },
    /** A task changed on the server. */
    receive({ state, commit }, message) {
        var _a, _b;
        const previous = state.messages.find(m => m.id === message.id);
        commit("upsertMessage", message);
        // A new task: show it starting.
        if (!previous && !isFinishedStatus(message.status)) {
            commit("setPanelOpen", true);
            return;
        }
        // A task finished: show the outcome, and tell the page on screen (it may want to refresh).
        if (isFinishedStatus(message.status) && (!previous || !isFinishedStatus(previous.status))) {
            commit("setPanelOpen", true);
            window.dispatchEvent(new CustomEvent(BackgroundTaskFinishedEvent, { detail: message }));
            if (message.status === BackgroundTaskStatus.Completed)
                Notifier.onOk(`${message.processName}: finished.`);
            else if (message.status === BackgroundTaskStatus.Cancelled)
                Notifier.onWarning(`${message.processName}: ${(_a = message.errorMessage) !== null && _a !== void 0 ? _a : "cancelled."}`);
            else
                Notifier.onWarning(`${message.processName}: ${(_b = message.errorMessage) !== null && _b !== void 0 ? _b : "finished with problems."} See Background tasks for details.`);
        }
    },
    openPanel({ commit }) {
        commit("setPanelOpen", true);
    },
    closePanel({ commit }) {
        commit("setPanelOpen", false);
    },
    togglePanel({ state, commit }) {
        commit("setPanelOpen", !state.panelOpen);
    },
    /** The user looked at a task's details: it no longer needs attention. */
    acknowledge({ commit }, id) {
        commit("acknowledge", id);
    },
    dismiss(_a, id_1) {
        return __awaiter(this, arguments, void 0, function* ({ commit }, id) {
            try {
                yield progressMessagesClient.delete(id);
                commit("removeMessages", [id]);
            }
            catch (ex) {
                Notifier.onServerError(ex);
            }
        });
    },
    dismissFinished(_a) {
        return __awaiter(this, arguments, void 0, function* ({ state, commit }) {
            try {
                yield progressMessagesClient.deleteFinished();
                commit("removeMessages", state.messages.filter(m => isFinishedStatus(m.status)).map(m => m.id));
            }
            catch (ex) {
                Notifier.onServerError(ex);
            }
        });
    },
    cancel(_a, id_1) {
        return __awaiter(this, arguments, void 0, function* ({ commit }, id) {
            commit("addCancelling", id);
            try {
                yield progressMessagesClient.cancel(id);
            }
            catch (ex) {
                commit("removeCancelling", id);
                Notifier.onServerError(ex);
            }
        });
    }
};
const namespaced = true;
export const backgroundTasksModule = {
    namespaced,
    state,
    actions,
    mutations
};
